/**
 * Dual Search Module
 * Handles camp title search + location-based search
 * Used when user searches for a specific camp name
 */

import { state } from "../core/state.js";
import { elements } from "../core/elements.js";
import { getIndex } from "./client.js";
import { searchCampByTitle } from "./camp-title-search.js";
import { buildFilters } from "../filters/filter-builder.js";
import { mergeFacetKeys } from "../utils/facet-utils.js";
import { getSelectedGender } from "../filters/gender-filter.js";
import { getCampStartDate, dateYmdToInt } from "../utils/date-utils.js";
import {
  showFilterMismatchBanner,
  hideFallbackBanner,
} from "../ui/banner-manager.js";

// Dependencies injected from index.js
let deps = {
  toggleSkeleton: null,
  renderCampCard: null,
  renderResults: null,
  updateURL: null,
  updateFilterUI: null,
  updateFiltersCount: null,
  runDecisionFlow: null,
};

/**
 * Set dependencies (called from index.js after modules loaded)
 * @param {Object} injectedDeps
 */
export function setDependencies(injectedDeps) {
  deps = { ...deps, ...injectedDeps };
}

/**
 * Check if any filters are currently active
 * @returns {boolean}
 */
export function hasActiveFilters() {
  return (
    state.sportSlug.length > 0 ||
    state.types.length > 0 ||
    state.gender.length > 0 ||
    state.filterCoed ||
    state.filterMaleOnly ||
    state.filterFemaleOnly ||
    (state.beginDates && state.endDates) ||
    state.age ||
    state.brand.length > 0 ||
    state.skills.length > 0 ||
    state.live === true ||
    (state.tier && state.tier.length > 0)
  );
}

/**
 * Check if camp matches active filters (for notification banner)
 * Checks: sport, dates, gender, age
 * @param {Object} camp - Camp object from Algolia
 * @returns {boolean} - true if matches all filters, false otherwise
 */
export function checkFiltersMatch(camp) {
  // Sport check
  if (state.sportSlug && state.sportSlug.length > 0) {
    const matchesSport = state.sportSlug.some(
      (slug) => camp.sportSlug === slug,
    );
    if (!matchesSport) return false;
  }

  // Date check using sessionStartDays (YYYYMMDD integers)
  // Camps without sessionStartDays are always included (no date data)
  if (state.beginDates && state.endDates) {
    const startDay = dateYmdToInt(state.beginDates);
    const endDay = dateYmdToInt(state.endDates);
    if (startDay && endDay && camp.sessionStartDays?.length) {
      const matches = camp.sessionStartDays.some((d) => d >= startDay && d <= endDay);
      if (!matches) return false;
    }
  }

  // Gender check
  const selectedGender = getSelectedGender();
  if (selectedGender) {
    if (selectedGender === "male") {
      const matchesGender =
        camp.gender &&
        (camp.gender.includes("male") || camp.gender.includes("all"));
      if (!matchesGender) return false;
    } else if (selectedGender === "female") {
      const matchesGender =
        camp.gender &&
        (camp.gender.includes("female") || camp.gender.includes("all"));
      if (!matchesGender) return false;
    }

    // Checkbox checks
    if (state.filterCoed) {
      const isCoed = camp.gender && camp.gender.includes("all");
      if (!isCoed) return false;
    }
    if (state.filterMaleOnly) {
      const isMaleOnly =
        camp.gender &&
        camp.gender.includes("male") &&
        !camp.gender.includes("all");
      if (!isMaleOnly) return false;
    }
    if (state.filterFemaleOnly) {
      const isFemaleOnly =
        camp.gender &&
        camp.gender.includes("female") &&
        !camp.gender.includes("all");
      if (!isFemaleOnly) return false;
    }
  }

  // Age check
  if (state.age) {
    if (state.age === "22+") {
      const matchesAge = camp.minAge >= 22 || camp.maxAge >= 22;
      if (!matchesAge) return false;
    } else {
      const age = parseInt(state.age, 10);
      if (!isNaN(age)) {
        const matchesAge = camp.minAge <= age && camp.maxAge >= age;
        if (!matchesAge) return false;
      }
    }
  }

  return true; // All checks passed
}

/**
 * Sort hits by current state sort setting
 * @param {Array} hits - Array of camp hits
 * @returns {Array} - Sorted array of camps
 */
export function sortHitsByState(hits) {
  if (state.sort === "title-asc") {
    hits.sort((a, b) =>
      (a.title || "")
        .toLowerCase()
        .localeCompare((b.title || "").toLowerCase()),
    );
  } else if (state.sort === "title-desc") {
    hits.sort((a, b) =>
      (b.title || "")
        .toLowerCase()
        .localeCompare((a.title || "").toLowerCase()),
    );
  } else if (state.sort === "date-asc") {
    hits.sort((a, b) => {
      const dateA = getCampStartDate(a);
      const dateB = getCampStartDate(b);
      if (!dateA && !dateB) {
        return (a.title || "")
          .toLowerCase()
          .localeCompare((b.title || "").toLowerCase());
      }
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA - dateB;
    });
  }
  // For distance sort, Algolia handles it automatically when aroundLatLng is set
  return hits;
}

/**
 * Search IN THE SAME LOCATION (250 mile radius) with filters
 * If NO sport filter selected -> use found camp's sport
 * If sport filter selected -> use selected sport (may differ from found camp)
 *
 * @param {Object} campCoordinates - { lat, lng, city, state, sportSlug } from found camp
 * @returns {Promise<Object>} - { hits, nbHits, facets, queryID, location }
 */
async function performLocationSearchWithFilters(campCoordinates) {
  const index = getIndex();

  console.log("🔍 [DUAL SEARCH] Executing location search:", {
    lat: campCoordinates.lat,
    lng: campCoordinates.lng,
    radius: "250 miles",
    foundCampSport: campCoordinates.sportSlug,
  });

  // Determine which sport to search for
  let sportFilterToUse = null;

  if (state.sportSlug && state.sportSlug.length > 0) {
    // User selected sport filter -> use it (may be different from found camp's sport)
    sportFilterToUse = state.sportSlug;
    console.log(
      "🔍 [DUAL SEARCH] Using selected sport filter:",
      sportFilterToUse,
    );
  } else {
    // NO sport filter -> use found camp's sport
    sportFilterToUse = [campCoordinates.sportSlug];
    console.log("🔍 [DUAL SEARCH] Using found camp sport:", sportFilterToUse);
  }

  // If user didn't select a sport, store in implicitSportSlug (not URL)
  // This ensures subsequent filter changes (brand, type) also filter by sport
  if (!state.sportSlug || state.sportSlug.length === 0) {
    state.implicitSportSlug = sportFilterToUse;
    console.log(
      "🔍 [DUAL SEARCH] Storing implicit sport (not in URL):",
      sportFilterToUse,
    );
  }

  // Temporarily use sportFilterToUse for buildFilters
  const originalSportSlug = state.sportSlug;
  state.sportSlug = sportFilterToUse;
  const filters = buildFilters();
  state.sportSlug = originalSportSlug; // Restore - don't persist to URL

  const radiusInMeters = 250 * 1609.34; // 250 miles to meters

  const searchParams = {
    filters: filters || undefined,
    hitsPerPage: 1000,
    page: 0,
    facets: ["sportSlug", "feeCodes", "gender", "brands", "skills", "live"],
    getRankingInfo: true,
    clickAnalytics: false,
    aroundLatLng: `${campCoordinates.lat},${campCoordinates.lng}`,
    aroundRadius: radiusInMeters,
  };

  try {
    const { queryID, hits, nbHits, facets } = await index.search(
      "",
      searchParams,
    );
    console.log(
      `🔍 [DUAL SEARCH] Location search: ${nbHits} camps within 250 miles`,
    );

    return {
      hits,
      nbHits,
      facets,
      queryID,
      location: {
        city: campCoordinates.city,
        state: campCoordinates.state,
      },
    };
  } catch (error) {
    console.error("❌ [DUAL SEARCH] Location search failed:", error);

    // Fallback: search by state without radius
    console.log("🔄 [DUAL SEARCH] Retrying without radius (state search)");

    const stateFilters = filters
      ? `${filters} AND (address.map.state:"${campCoordinates.state}" OR address.state:"${campCoordinates.state}")`
      : `address.map.state:"${campCoordinates.state}" OR address.state:"${campCoordinates.state}"`;

    const fallbackParams = {
      filters: stateFilters,
      hitsPerPage: 1000,
      page: 0,
      facets: ["sportSlug", "feeCodes", "gender", "brands", "skills", "live"],
      clickAnalytics: false,
    };

    try {
      const fallbackResult = await index.search("", fallbackParams);
      console.log(
        `🔍 [DUAL SEARCH] Fallback state search: ${fallbackResult.nbHits} camps`,
      );
      return {
        ...fallbackResult,
        location: {
          city: campCoordinates.city,
          state: campCoordinates.state,
        },
      };
    } catch (fallbackError) {
      console.error("❌ [DUAL SEARCH] Fallback also failed:", fallbackError);
      return { hits: [], nbHits: 0, facets: {}, queryID: null };
    }
  }
}

/**
 * Insert found camp card before results header
 * @param {Object} camp - Camp hit from Algolia
 */
function insertFoundCampCard(camp) {
  if (!deps.renderCampCard) return;

  const currentMatchCampEls = document.querySelectorAll(".rd-search-results__camp-match-title, .rd-search-results__found-camp-block");
  if(currentMatchCampEls.length > 0) {
    currentMatchCampEls.forEach(el => el.remove());
  }

  const campMatchLabel = document.createElement("div");
  campMatchLabel.className =
    "rd-search-results__camp-match-title heading-h5 capsize";
  campMatchLabel.textContent = "camp match";

  const campBlock = document.createElement("div");
  campBlock.className = "rd-search-results__found-camp-block";
  campBlock.innerHTML = deps.renderCampCard(camp);

  const header = document.querySelector(".rd-search-results__content-header");
  if (header && header.parentNode) {
    header.parentNode.insertBefore(campBlock, header);
    header.parentNode.insertBefore(campMatchLabel, campBlock);
  }
}

/**
 * Perform dual search: camp title + location search
 * Runs when specific camp is found (isPlace = true), regardless of filters
 * Renders results in TWO sections with location-based title
 * @param {string} campTitle - Camp title to search for
 * @returns {Promise<void>}
 */
export async function performDualSearch(campTitle) {
  console.log("🔍 [DUAL SEARCH] Starting dual search for:", campTitle);

  if (deps.toggleSkeleton) {
    deps.toggleSkeleton(true);
  }

  try {
    // Step 1: Search by camp title FIRST (not in parallel!)
    // Search WITHOUT sport filter so specific camp is always found
    // (e.g., lacrosse camp is found even when sport filter is football)
    // Sport filter is applied to Section 2 (nearby camps) only
    const campSearchResult = await searchCampByTitle(
      campTitle,
      [],
      state
    );

    if (!campSearchResult?.firstCamp) {
      console.warn(
        "⚠️ [DUAL SEARCH] No camp found, falling back to decision flow",
      );
      if (deps.toggleSkeleton) deps.toggleSkeleton(false);
      if (deps.runDecisionFlow) deps.runDecisionFlow();
      return;
    }

    // Step 2: Search IN SAME LOCATION with 250 mile radius
    const campCoordinates = {
      lat: campSearchResult.firstCamp.lat,
      lng: campSearchResult.firstCamp.lng,
      city: campSearchResult.firstCamp.city,
      state: campSearchResult.firstCamp.state,
      sportSlug: campSearchResult.firstCamp.camp.sportSlug,
    };

    const locationSearchResult =
      await performLocationSearchWithFilters(campCoordinates);

    console.log("🔍 [DUAL SEARCH] Results:", {
      campSearch: campSearchResult?.hits?.length || 0,
      locationSearch: locationSearchResult?.hits?.length || 0,
    });

    // Mark that camp was found via title search (for heading logic when filters change)
    state.geocodeResult = {
      ...state.geocodeResult,
      isCampFound: true,
    };

    // Save location from dual search for heading when filters change
    if (locationSearchResult.location) {
      state.dualSearchLocation = {
        city: locationSearchResult.location.city,
        state: locationSearchResult.location.state,
      };
    }

    // Process Section 1: Found camp (DON'T apply filters - always show)
    let section1Hits = campSearchResult.hits || [];

    // When title search returns multiple camps (e.g. "sacred heart university" → 11 camps)
    // and a sport filter is active, narrow Section 1 to only matching sport.
    // This prevents false "filters don't match" banner when Step 2 already found
    // the right camp but performDualSearch re-searched without sport filter.
    // For truly unique camps (1 hit), keep original behavior (mismatch banner if sport differs).
    if (state.sportSlug?.length > 0 && section1Hits.length > 1) {
      const sportMatched = section1Hits.filter((hit) =>
        state.sportSlug.includes(hit.sportSlug)
      );
      if (sportMatched.length > 0) {
        section1Hits = sportMatched;
      }
    }

    // Check if camp matches filters (for notification banner) - only if filters are active
    const hasFilters = hasActiveFilters();
    let shouldShowFilterMismatchBanner = false;

    section1Hits.forEach((hit) => {
      if (hasFilters) {
        const filtersMatch = checkFiltersMatch(hit);
        if (!filtersMatch) {
          shouldShowFilterMismatchBanner = true;
        }
      }
    });

    // Save first camp's objectID to exclude from subsequent searches
    if (section1Hits.length > 0 && section1Hits[0].objectID) {
      state.foundCampObjectID = section1Hits[0].objectID;
      state.foundCampHit = section1Hits[0]; // Save full hit for mismatch re-evaluation on filter changes
    }

    console.log(
      "🔍 [DUAL SEARCH] Section 1 (found camp):",
      section1Hits.length,
    );

    // Process Section 2: Other camps in same location
    let section2Hits = [];

    if (locationSearchResult && locationSearchResult.hits) {
      // Algolia already applied location + sport filters
      // Exclude only the FIRST camp (shown in "camp match"), not all title matches
      const firstCampObjectID = section1Hits[0]?.objectID;
      section2Hits = locationSearchResult.hits.filter(
        (hit) => hit.objectID !== firstCampObjectID,
      );

      console.log(
        "🔍 [DUAL SEARCH] Section 2 (location camps):",
        section2Hits.length,
        "excluded:",
        firstCampObjectID,
      );
    }

    // Set coordinates from first camp for distance calculations
    state.coordinates = {
      lat: campCoordinates.lat,
      lng: campCoordinates.lng,
    };

    // Sort Section 2 results
    section2Hits = sortHitsByState(section2Hits);

    // Store results
    state.allFilteredHits = [...section1Hits, ...section2Hits];
    state.totalHits = state.allFilteredHits.length;
    state.displayedHits = state.hitsPerPage;

    // Update facets from location search
    if (locationSearchResult.facets) {
      state.facets = {
        sportSlug: locationSearchResult.facets.sportSlug || {},
        types: locationSearchResult.facets.feeCodes || {},
        gender: locationSearchResult.facets.gender || {},
        brands: locationSearchResult.facets.brands || {},
        skills: locationSearchResult.facets.skills || {},
        live: locationSearchResult.facets.live || {},
      };

      // Save initial facets as allFacets so filter options don't disappear
      // when user changes filters (mergeFacetKeys needs allFacets as baseline)
      state.allFacets.feeCodes = mergeFacetKeys(
        state.allFacets.feeCodes || {},
        locationSearchResult.facets.feeCodes || {}
      );
      state.allFacets.brands = mergeFacetKeys(
        state.allFacets.brands || {},
        locationSearchResult.facets.brands || {}
      );
      state.allFacets.skills = mergeFacetKeys(
        state.allFacets.skills || {},
        locationSearchResult.facets.skills || {}
      );
    }

    // Render found camp BEFORE main results
    if (deps.toggleSkeleton) deps.toggleSkeleton(false);

    // Show filter mismatch banner if needed
    showFilterMismatchBanner(shouldShowFilterMismatchBanner);

    // Insert found camp card
    if (section1Hits.length > 0) {
      insertFoundCampCard(section1Hits[0]);
    }

    // Build location-based label for heading (sport is added by renderResults)
    let labelTemplate = null;
    if (locationSearchResult.location && locationSearchResult.location.city) {
      labelTemplate = `${locationSearchResult.location.city}, ${locationSearchResult.location.state}: {count} camps found`;
    }

    // Render Section 2 using standard renderResults()
    const hitsToDisplay = section2Hits.slice(0, state.displayedHits);
    if (deps.renderResults) {
      deps.renderResults(
        hitsToDisplay,
        section2Hits.length,
        false,
        labelTemplate,
      );
    }

    if (deps.updateURL) deps.updateURL();
    if (deps.updateFilterUI) deps.updateFilterUI();
    // Dual-search render bypasses performSearch — refresh "Filters (N)" here too
    if (deps.updateFiltersCount) deps.updateFiltersCount();
  } catch (error) {
    console.error("❌ [DUAL SEARCH] Error:", error);
    if (deps.toggleSkeleton) deps.toggleSkeleton(false);
    if (deps.runDecisionFlow) deps.runDecisionFlow();
  }
}

/**
 * Clear dual search state (called when starting new search)
 */
export function clearDualSearchState() {
  state.implicitSportSlug = null;
  state.dualSearchLocation = null;
  state.foundCampObjectID = null;
}

console.log("📦 Dual search module loaded");
